import { WC_LIMITS as L } from './WebCodecsTypes';
import type { WcConfig, WcControl, WcFeedback, WcFragment } from './WebCodecsTypes';

type RecordValue = Record<string, unknown>;

/** Stateless validation: authentication, epoch ordering and issued-packet accounting belong to the session. */
class WcValidation {
  static Integer(value: unknown, max = L.maxTime, min = 0): value is number {
    return typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
  }
  static Shape(value: unknown, fields: string): value is RecordValue {
    const keys = fields.split(' ');
    return value !== null && typeof value === 'object' && !Array.isArray(value) &&
      Object.keys(value).length === keys.length && keys.every(key => Object.prototype.hasOwnProperty.call(value, key));
  }
  static Text(value: unknown, max: number): value is string {
    // Reject unpaired UTF-16 surrogates, including those escaped inside otherwise valid UTF-8 JSON.
    return typeof value === 'string' && value.length <= max &&
      !/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/u.test(value);
  }
  static Envelope(value: unknown): value is RecordValue {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const packet = value as RecordValue;
    return packet['v'] === 1 && typeof packet['session'] === 'string' &&
      /^native-player-[a-zA-Z0-9-]{1,80}$/.test(packet['session']);
  }
  static Config(value: unknown): value is WcConfig {
    if (!this.Shape(value, 'epoch codec codecString format width height fps bitDepth primaries transfer matrix fullRange')) return false;
    return this.Integer(value['epoch'], L.maxId, 1) && ['h264', 'hevc', 'av1'].includes(value['codec'] as string) &&
      typeof value['codecString'] === 'string' && /^[\x21-\x7e]{1,128}$/.test(value['codecString']) &&
      value['format'] === (value['codec'] === 'av1' ? 'av1-obu' : 'annexb') &&
      this.Integer(value['width'], 4096, 16) && this.Integer(value['height'], 4096, 16) &&
      typeof value['fps'] === 'number' && Number.isFinite(value['fps']) && value['fps'] >= 1 && value['fps'] <= 120 &&
      value['bitDepth'] === 8 && ['primaries', 'transfer', 'matrix'].every(key => value[key] === 'bt709') &&
      typeof value['fullRange'] === 'boolean';
  }
  static Control(value: unknown): value is WcControl {
    if (!this.Envelope(value)) return false;
    const shape = (fields: string) => this.Shape(value, `v type session ${fields}`);
    const epoch = () => this.Integer(value['epoch'], L.maxId, 1);
    switch (value['type']) {
      case 'configure': return shape('config') && this.Config(value['config']);
      case 'configured': return shape('epoch') && epoch();
      case 'rejected': return shape('epoch reason') && epoch() && this.Text(value['reason'], 256);
      case 'keyframe': return shape('epoch afterFrameId') && epoch() && this.Integer(value['afterFrameId'], L.maxId);
      case 'source': return shape('epoch latestFrameId state') && epoch() && this.Integer(value['latestFrameId'], L.maxId) &&
        ['active', 'idle', 'paused'].includes(value['state'] as string);
      case 'ping': return shape('probeId hostSendUs') && this.Integer(value['probeId'], L.maxId) && this.Integer(value['hostSendUs']);
      case 'pong': return shape('probeId hostSendUs clientReceiveUs clientSendUs') &&
        this.Integer(value['probeId'], L.maxId) && this.Integer(value['hostSendUs']) &&
        this.Integer(value['clientReceiveUs']) && this.Integer(value['clientSendUs']) && value['clientSendUs'] >= value['clientReceiveUs'];
      case 'close': return shape('reason') && this.Text(value['reason'], 256);
      default: return false;
    }
  }
  static Feedback(value: unknown): value is WcFeedback {
    const fields = 'v type session epoch sequence clientNowUs highestPacketId receivedRanges receivedBytes arrivalSamples ' +
      'missingFragments retiredThroughFrameId decodedFrameId renderedFrameId receiverWindowBytes reassemblyBytes ' +
      'decodeQueueSize decoderInFlight renderPending oldestPendingAgeUs decodeP95Us renderIntervalP95Us visible needsKeyframe';
    if (!this.Envelope(value) || !this.Shape(value, fields) || value['type'] !== 'feedback') return false;
    if (!this.Integer(value['epoch'], L.maxId, 1) ||
      !['sequence', 'highestPacketId', 'retiredThroughFrameId', 'decodedFrameId', 'renderedFrameId'].every(k => this.Integer(value[k], L.maxId)) ||
      !['clientNowUs', 'receivedBytes', 'oldestPendingAgeUs', 'decodeP95Us', 'renderIntervalP95Us'].every(k => this.Integer(value[k])) ||
      !['receiverWindowBytes', 'reassemblyBytes'].every(k => this.Integer(value[k], L.windowBytes)) ||
      !['decodeQueueSize', 'decoderInFlight'].every(k => this.Integer(value[k], 2)) || !this.Integer(value['renderPending'], 1) ||
      typeof value['visible'] !== 'boolean' || typeof value['needsKeyframe'] !== 'boolean') return false;
    return this.FeedbackLists(value as unknown as WcFeedback);
  }
  private static FeedbackLists(value: WcFeedback): boolean {
    let previous = 0;
    if (!Array.isArray(value.receivedRanges) || value.receivedRanges.length > 64 ||
      !value.receivedRanges.every(range => {
        if (!Array.isArray(range) || range.length !== 2 || !this.Integer(range[0], L.maxId, 1) ||
          !this.Integer(range[1], value.highestPacketId, range[0]) || range[0] <= previous) return false;
        previous = range[1]; return true;
      })) return false;
    const packets = new Set<number>();
    if (!Array.isArray(value.arrivalSamples) || value.arrivalSamples.length > 16 ||
      !value.arrivalSamples.every(sample => {
        if (!this.Shape(sample, 'packetId arrivalUs') || !this.Integer(sample.packetId, value.highestPacketId, 1) ||
          !this.Integer(sample.arrivalUs, value.clientNowUs) || packets.has(sample.packetId) ||
          !value.receivedRanges.some(([lo, hi]) => sample.packetId >= lo && sample.packetId <= hi)) return false;
        packets.add(sample.packetId); return true;
      })) return false;
    const frames = new Set<number>(); let indices = 0;
    return Array.isArray(value.missingFragments) && value.missingFragments.length <= 4 && value.missingFragments.every(missing => {
      if (!this.Shape(missing, 'frameId indices remainingUs') || !this.Integer(missing.frameId, L.maxId, 1) ||
        missing.frameId <= value.retiredThroughFrameId || frames.has(missing.frameId) || !this.Integer(missing.remainingUs) ||
        !Array.isArray(missing.indices) || missing.indices.length < 1 || missing.indices.length > 32 ||
        !missing.indices.every(index => this.Integer(index, L.fragmentCount - 1)) ||
        new Set(missing.indices).size !== missing.indices.length) return false;
      frames.add(missing.frameId); indices += missing.indices.length; return indices <= 32;
    });
  }
  static Fragment(value: WcFragment): boolean {
    const size = value.payload?.byteLength;
    return value.payload instanceof Uint8Array && this.Integer(size, L.messageBytes - L.headerBytes, 1) &&
      [value.epoch, value.frameId, value.packetId].every(id => this.Integer(id, L.maxId, 1)) &&
      this.Integer(value.fragmentCount, L.fragmentCount, 1) && this.Integer(value.fragmentIndex, value.fragmentCount - 1) &&
      this.Integer(value.frameBytes, L.frameBytes, 1) && this.Integer(value.fragmentOffset, value.frameBytes - size) &&
      value.frameBytes >= value.fragmentCount && value.frameBytes <= value.fragmentCount * (L.messageBytes - L.headerBytes) &&
      value.fragmentOffset <= value.fragmentIndex * (L.messageBytes - L.headerBytes) &&
      value.frameBytes - value.fragmentOffset - size <= (value.fragmentCount - value.fragmentIndex - 1) * (L.messageBytes - L.headerBytes) &&
      value.fragmentOffset >= value.fragmentIndex && value.frameBytes - value.fragmentOffset - size >= value.fragmentCount - value.fragmentIndex - 1 &&
      (value.fragmentIndex !== 0 || value.fragmentOffset === 0) &&
      (value.fragmentIndex !== value.fragmentCount - 1 || value.fragmentOffset + size === value.frameBytes) &&
      this.Integer(value.captureUs) && this.Integer(value.sentUs) && value.sentUs >= value.captureUs &&
      this.Integer(value.durationUs, L.maxId) && typeof value.key === 'boolean';
  }
}

/** Pure bounded codec. Successful parsing conveys no authority or session-state transition. */
export class WebCodecsWire {
  static EncodeFrameFragment(value: WcFragment): Uint8Array {
    if (!value || !WcValidation.Fragment(value)) throw new TypeError('Invalid WebCodecs fragment');
    const bytes = new Uint8Array(L.headerBytes + value.payload.byteLength);
    const view = new DataView(bytes.buffer);
    view.setUint32(0, 0x50435743); view.setUint8(4, 1); view.setUint8(5, value.key ? 1 : 0); view.setUint16(6, L.headerBytes);
    view.setUint32(8, value.epoch); view.setUint32(12, value.frameId);
    view.setUint16(16, value.fragmentIndex); view.setUint16(18, value.fragmentCount);
    view.setUint32(20, value.frameBytes); view.setUint32(24, value.fragmentOffset); view.setUint16(28, value.payload.byteLength);
    view.setBigUint64(32, BigInt(value.captureUs)); view.setBigUint64(40, BigInt(value.sentUs));
    view.setUint32(48, value.durationUs); view.setUint32(52, value.packetId);
    bytes.set(value.payload, L.headerBytes); return bytes;
  }
  static DecodeFrameFragment(bytes: Uint8Array): WcFragment | null {
    if (!(bytes instanceof Uint8Array) || bytes.byteLength <= L.headerBytes || bytes.byteLength > L.messageBytes) return null;
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(0) !== 0x50435743 || view.getUint8(4) !== 1 || view.getUint8(5) > 1 ||
        view.getUint16(6) !== L.headerBytes || view.getUint16(30) !== 0 || view.getUint16(28) !== bytes.byteLength - L.headerBytes) return null;
    const value: WcFragment = {
      epoch: view.getUint32(8), frameId: view.getUint32(12), fragmentIndex: view.getUint16(16), fragmentCount: view.getUint16(18),
      frameBytes: view.getUint32(20), fragmentOffset: view.getUint32(24), captureUs: Number(view.getBigUint64(32)),
      sentUs: Number(view.getBigUint64(40)), durationUs: view.getUint32(48), packetId: view.getUint32(52),
      key: view.getUint8(5) === 1, payload: bytes.subarray(L.headerBytes),
    };
    if (!WcValidation.Fragment(value)) return null;
    // Ownership is independent of a transport buffer reused after this call.
    value.payload = value.payload.slice(); return value;
  }
  static EncodeControl(value: WcControl): Uint8Array {
    if (!WcValidation.Control(value)) throw new TypeError('Invalid WebCodecs control');
    return this.EncodeJson(value, L.controlBytes);
  }
  static DecodeControl(bytes: Uint8Array): WcControl | null {
    const value = this.DecodeJson(bytes, L.controlBytes);
    return WcValidation.Control(value) ? value : null;
  }
  static EncodeFeedback(value: WcFeedback): Uint8Array {
    if (!WcValidation.Feedback(value)) throw new TypeError('Invalid WebCodecs feedback');
    return this.EncodeJson(value, L.feedbackBytes);
  }
  static DecodeFeedback(bytes: Uint8Array): WcFeedback | null {
    const value = this.DecodeJson(bytes, L.feedbackBytes);
    return WcValidation.Feedback(value) ? value : null;
  }
  private static EncodeJson(value: WcControl | WcFeedback, limit: number): Uint8Array {
    const bytes = new TextEncoder().encode(JSON.stringify(value));
    if (bytes.length > limit) throw new TypeError('Oversized WebCodecs message');
    return bytes;
  }
  private static DecodeJson(bytes: Uint8Array, limit: number): unknown {
    if (!(bytes instanceof Uint8Array) || !bytes.length || bytes.length > limit) return null;
    try {
      // Preserve BOM so JSON.parse rejects it consistently with the C++ wire boundary.
      const text = new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(bytes);
      let depth = 0; let quoted = false; let escaped = false;
      for (const ch of text) {
        if (quoted) { if (escaped) escaped = false; else if (ch === '\\') escaped = true; else if (ch === '"') quoted = false; }
        else if (ch === '"') quoted = true;
        else if (ch === '{' || ch === '[') { if (++depth > 8) return null; }
        else if (ch === '}' || ch === ']') --depth;
      }
      return JSON.parse(text);
    } catch { return null; }
  }
}
